import React, { useEffect, useRef, useState } from 'react';
import { View, TextInput, Text as RNText } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { type BottomTabNavigationProp } from '@react-navigation/bottom-tabs';
import type { TNavigationStackWithParams } from 'app/types/navigation';
import { MainLayout } from '@shared/components/md-design-system/Layout/MainLayout';
import { Text } from '@shared/components/text';
import { Head } from '@shared/components/head';
import Button from '@shared/components/md-design-system/Button/Button';
import ConfirmModal from '@shared/components/md-design-system/ConfirmModal/ConfirmModal';
import { colorPalette } from '@shared/config/colorsConfig';
import { dynamicTranslations } from '@shared/utils/dynamicTranslation';
import SelectField from '@md-design-system/SelectField/SelectField';
import { useTeleassistanceRequestForm } from '../../hooks/useTeleassistanceRequestForm';
import type {
  TeleassistancePreferredDate,
  TeleassistancePreferredTime,
} from 'app/_modules/pathways';
import SuccessView from './SuccessView';

type TNav = NativeStackNavigationProp<TNavigationStackWithParams<any>>;

const TeleassistanceRequestScreen: React.FC = () => {
  const { t } = dynamicTranslations();
  const navigation = useNavigation<TNav>();
  const s = 'teleassistance.form';

  const [showExitModal, setShowExitModal] = useState(false);
  const onPendingExit = useRef<(() => void) | null>(null);

  const {
    exams,
    examId,
    setExamId,
    preferredDate,
    setPreferredDate,
    preferredTime,
    setPreferredTime,
    additionalDetails,
    setAdditionalDetails,
    formTouched,
    isFormComplete,
    isSubmitting,
    isSubmitted,
    handleSubmit,
  } = useTeleassistanceRequestForm();

  useEffect(() => {
    if (isSubmitted) return;

    const unsubscribeStack = navigation.addListener('beforeRemove', (e) => {
      if (!formTouched) return;
      e.preventDefault();
      onPendingExit.current = () => navigation.dispatch(e.data.action);
      setShowExitModal(true);
    });

    const parent = navigation.getParent()?.getParent() as BottomTabNavigationProp<any> | undefined;
    const unsubscribeTab = parent?.addListener('tabPress', (e) => {
      if (!formTouched) return;
      e.preventDefault();
      onPendingExit.current = () => {
        navigation.goBack();
      };
      setShowExitModal(true);
    });

    return () => {
      unsubscribeStack();
      unsubscribeTab?.();
    };
  }, [navigation, formTouched, isSubmitted]);

  const handleConfirmExit = () => {
    setShowExitModal(false);
    onPendingExit.current?.();
    onPendingExit.current = null;
  };

  const handleCancelExit = () => {
    setShowExitModal(false);
    onPendingExit.current = null;
  };

  const examOptions = exams.map(exam => ({ value: exam.id, label: exam.label }));

  const preferredDateOptions = [
    { value: 'asap', label: t(`${s}.preferredDate.asap`) },
    { value: 'next_few_days', label: t(`${s}.preferredDate.next_few_days`) },
    { value: 'next_week', label: t(`${s}.preferredDate.next_week`) },
    { value: 'indifferent', label: t(`${s}.preferredDate.indifferent`) },
  ];

  const preferredTimeOptions = [
    { value: 'morning', label: t(`${s}.preferredTime.morning`) },
    { value: 'afternoon', label: t(`${s}.preferredTime.afternoon`) },
    { value: 'indifferent', label: t(`${s}.preferredTime.indifferent`) },
  ];

  if (isSubmitted) {
    return (
      <MainLayout navigation={() => navigation.goBack()}>
        <SuccessView onGoBack={() => {
          navigation.goBack();
          navigation.navigate('core', { screen: 'Homepage' });
        }} />
      </MainLayout>
    );
  }

  return (
    <MainLayout navigation={() => navigation.goBack()} scrollable>
      <Head type="h2" className="text-primary-900 mb-2 mt-4">{t(`${s}.title`)}</Head>
      <RNText className="text-base text-gray-600 mb-8">
        {t(`${s}.subtitlePre`)}
        <RNText
          style={{ color: colorPalette.primary[700], textDecorationLine: 'underline', fontWeight: '500' }}
          onPress={() => navigation.navigate('FAQ')}
        >
          {t(`${s}.subtitleFaqLink`)}
        </RNText>
        {t(`${s}.subtitlePost`)}
      </RNText>

      <SelectField
        label={t(`${s}.reasonLabel`)}
        placeholder={t(`${s}.reasonPlaceholder`)}
        value={examId}
        options={examOptions}
        onChange={(v) => setExamId(v)}
      />

      <SelectField
        label={t(`${s}.preferredDateLabel`)}
        placeholder={t(`${s}.preferredDatePlaceholder`)}
        value={preferredDate}
        options={preferredDateOptions}
        onChange={(v) => setPreferredDate(v as TeleassistancePreferredDate)}
      />

      <SelectField
        label={t(`${s}.preferredTimeLabel`)}
        placeholder={t(`${s}.preferredTimePlaceholder`)}
        value={preferredTime}
        options={preferredTimeOptions}
        onChange={(v) => setPreferredTime(v as TeleassistancePreferredTime)}
      />

      <View className="mb-6">
        <Text className="text-base font-semibold text-gray-700 mb-1">{t(`${s}.detailsLabel`)}</Text>
        <TextInput
          className="border border-gray-300 rounded-xl px-4 py-3 text-base font-body text-gray-900 bg-white"
          placeholder={t(`${s}.detailsPlaceholder`)}
          placeholderTextColor={colorPalette.gray[400]}
          value={additionalDetails}
          onChangeText={setAdditionalDetails}
          multiline
          numberOfLines={4}
          textAlignVertical="top"
          style={{ minHeight: 100 }}
        />
      </View>

      <Button
        variant="primary"
        onPress={handleSubmit}
        disabled={!isFormComplete || isSubmitting}
      >
        {isSubmitting ? t(`${s}.submitting`) : t(`${s}.submitButton`)}
      </Button>

      <ConfirmModal
        visible={showExitModal}
        title={t(`${s}.exitModal.title`)}
        message={t(`${s}.exitModal.message`)}
        confirmLabel={t(`${s}.exitModal.exit`)}
        cancelLabel={t(`${s}.exitModal.stay`)}
        confirmVariant="danger-outline"
        onConfirm={handleConfirmExit}
        onCancel={handleCancelExit}
      />
    </MainLayout>
  );
};

export default TeleassistanceRequestScreen;
